import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  MenuItem,
  Popover,
  Select,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { paths } from 'src/paths';
import { mdsDashboardPrintNewTreatment } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { RouterLink } from 'src/components/router-link';
import { setMdsWorksheetDirtyAction } from 'src/redux/actions/mdsDashboardAction';
import PageLeaveConfirmModal from 'src/components/common-modals/page-leave-confirmation';
import { BootstrapDialog } from 'src/components/common-modals/BootstrapDialog';
import { useMdsWorksheet } from './use-mds-worksheet';
import { useMdsWorksheetEditor } from './use-mds-worksheet-editor';
import { useMdsWorksheetAudit } from './use-mds-worksheet-audit';
import NewWorksheetDialog from './new-worksheet-dialog';
import SubmitWorksheetDialog from './submit-worksheet-dialog';
import VersionHistoryDialog from './version-history-dialog';
import ApplyDocumentDialog from './apply-document-dialog';
import WorksheetAuditDrawer from './worksheet-audit-drawer';
import {
  assessmentTypeLabel,
  actorLabel,
  candidateLevels,
  collectApplyDocuments,
  completeByLabel,
  docTypeLabel,
  editableCompletion,
  latestSubmissionVersion,
  sectionCompletion,
  shortDate,
  sourceBadge,
  valueDisplay,
  type MdsDocGroup,
} from './worksheet-utils';
import {
  getMdsSubmissionItems,
  printMdsWorksheet,
  submitMdsWorksheet,
  type MdsConflictItem,
  type MdsGgCandidate,
  type MdsItemCandidate,
  type MdsWorksheetColumn,
  type MdsWorksheetItem,
  type MdsWorksheetSection,
  type MdsWorksheetVersion,
} from 'src/services/documentService/mdsWorksheet';

/**
 * BRK-1852/BRK-1854: the MDS worksheet (contract v1.1 §5.4 / UX spec M2.5).
 * Sections the caller may edit (canEdit from the server) render score
 * dropdowns; other sections stay read-only and collapsed with a lock. Edits
 * are CLIENT-ONLY drafts (locked decision on BRK-1853 — no server drafts), so
 * every exit while dirty routes through the leave-confirmation flow:
 * in-page exits via the local dialog here, sidebar navigation via the
 * mdsWorksheetDirty redux flag + top-nav dialog, and refresh/close via
 * beforeunload. Submit itself lands with BRK-1856 on top of
 * {@link useMdsWorksheetEditor}'s buildSubmissionItems.
 */

type Editor = ReturnType<typeof useMdsWorksheetEditor>;

const ItemRow: React.FC<{
  item: MdsWorksheetItem;
  scoreOptions: Record<string, { value: string; description: string }[]>;
  canEdit: boolean;
  editor: Editor;
  onOpenAudit: (itemCode: string, mdsColumn: string, label: string, scoreType: string) => void;
}> = ({ item, scoreOptions, canEdit, editor, onOpenAudit }) => {
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const column: MdsWorksheetColumn | undefined = item.columns[0];
  const badge = sourceBadge(column);
  const dirty = column ? editor.isItemDirty(item.itemCode, column.mdsColumn) : false;
  const draftSource = column ? editor.getDraftSource(item.itemCode, column.mdsColumn) : null;
  const options = scoreOptions[item.scoreType] || [];
  const editValue = column ? editor.displayValue(item.itemCode, column.mdsColumn) : '';
  // a stored legacy/dash code absent from the catalog must stay visible and
  // selected, not silently render as blank (indistinguishable from cleared)
  const unmatchedValue = editValue !== '' && !options.some((option) => option.value === editValue);
  // BRK-1855: document values this item can be pulled from (editable rows only)
  const candidates: MdsGgCandidate[] = canEdit && column ? item.ggCandidates : [];
  // BRK-1866: Section K mds_item_collected candidates (editable rows only)
  const mdsCandidates: MdsItemCandidate[] = canEdit && column ? item.mdsCandidates : [];

  return (
    <TableRow hover>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography
          variant="subtitle2"
          component="span"
        >
          {item.itemCode}
        </Typography>
        {item.usedForFunctionScore && (
          <Tooltip title="Contributes to the PDPM GG function score">
            <Typography
              component="span"
              color="text.secondary"
            >
              {' Σ'}
            </Typography>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>{item.label}</TableCell>
      <TableCell>
        {item.discipline && (
          <Chip
            size="small"
            variant="outlined"
            label={item.discipline}
          />
        )}
      </TableCell>
      <TableCell>
        {canEdit && column ? (
          <Stack
            direction="row"
            gap={0.5}
            alignItems="center"
          >
            <Select
              size="small"
              value={editValue}
              onChange={(e) => editor.setValue(item.itemCode, column.mdsColumn, e.target.value)}
              displayEmpty
              sx={{ minWidth: 280 }}
              SelectDisplayProps={{ 'aria-label': `Value for ${item.itemCode}` }}
            >
              <MenuItem value="">
                <Typography
                  component="span"
                  color="text.secondary"
                >
                  — Not assessed —
                </Typography>
              </MenuItem>
              {options.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                >
                  {option.value} — {option.description}
                </MenuItem>
              ))}
              {unmatchedValue && (
                <MenuItem
                  value={editValue}
                  disabled
                >
                  {editValue} (unrecognized)
                </MenuItem>
              )}
            </Select>
            {(candidates.length > 0 || mdsCandidates.length > 0) && (
              <Tooltip title="Apply a value from a document">
                <IconButton
                  size="small"
                  aria-label={`Apply from document for ${item.itemCode}`}
                  onClick={(e) => setPickerAnchor(e.currentTarget)}
                >
                  <DescriptionOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Popover
              open={Boolean(pickerAnchor)}
              anchorEl={pickerAnchor}
              onClose={() => setPickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Stack sx={{ p: 1, maxWidth: 360 }}>
                {candidates.flatMap((candidate) =>
                  candidateLevels(candidate).map((level) => (
                    <MenuItem
                      key={`${candidate.ggCollectedId}|${level.levelType}`}
                      onClick={() => {
                        editor.applyGgValue(item.itemCode, column.mdsColumn, {
                          value: level.value,
                          ggCollectedId: candidate.ggCollectedId,
                          levelType: level.levelType,
                          documentType: candidate.documentType,
                          dateOfService: candidate.dateOfService,
                        });
                        setPickerAnchor(null);
                      }}
                    >
                      <Stack
                        direction="row"
                        gap={1}
                        alignItems="center"
                      >
                        <Typography variant="body2">
                          {level.label} {level.value} · {docTypeLabel(candidate.documentType)} ·{' '}
                          {shortDate(candidate.dateOfService)}
                          {candidate.discipline ? ` · ${candidate.discipline}` : ''}
                        </Typography>
                        {candidate.inObservationWindow === false && (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label="Out of window"
                          />
                        )}
                      </Stack>
                    </MenuItem>
                  ))
                )}
                {/* BRK-1866: Section K candidates — a single value per document */}
                {mdsCandidates.map((candidate) => (
                  <MenuItem
                    key={`mds|${candidate.mdsItemCollectedId}`}
                    onClick={() => {
                      editor.applyMdsValue(item.itemCode, column.mdsColumn, {
                        value: candidate.value,
                        mdsItemCollectedId: candidate.mdsItemCollectedId,
                        formSubmissionUuid: candidate.formSubmissionUuid,
                        documentType: candidate.documentType,
                        dateOfService: candidate.dateOfService,
                      });
                      setPickerAnchor(null);
                    }}
                  >
                    <Stack
                      direction="row"
                      gap={1}
                      alignItems="center"
                    >
                      <Typography variant="body2">
                        {candidate.value} · {docTypeLabel(candidate.documentType)} ·{' '}
                        {shortDate(candidate.dateOfService)}
                        {candidate.discipline ? ` · ${candidate.discipline}` : ''}
                      </Typography>
                      {candidate.inObservationWindow === false && (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label="Out of window"
                        />
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Stack>
            </Popover>
          </Stack>
        ) : (
          valueDisplay(column, scoreOptions[item.scoreType])
        )}
      </TableCell>
      <TableCell>
        <Stack
          direction="row"
          gap={0.5}
          alignItems="center"
        >
          {/* pending draft provenance wins over the (now-stale) saved badge */}
          {dirty &&
            (draftSource?.type === 'GG_COLLECTED' || draftSource?.type === 'MDS_COLLECTED') && (
              <Tooltip title="From the selected document — not submitted yet">
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  label={`${docTypeLabel(draftSource.documentType)} · ${shortDate(
                    draftSource.dateOfService
                  )}`}
                />
              </Tooltip>
            )}
          {!dirty && badge && (
            <Tooltip
              title={
                badge.outOfWindow
                  ? 'Source document date falls outside the observation window for this ARD'
                  : ''
              }
            >
              <Chip
                size="small"
                label={badge.label}
                color={badge.outOfWindow ? 'warning' : 'default'}
                variant={badge.outOfWindow ? 'filled' : 'outlined'}
              />
            </Tooltip>
          )}
          {dirty && (
            <Tooltip title="Edited — not submitted yet">
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label="Unsaved"
              />
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      <TableCell align="right">
        {column?.fromVersion != null && (
          <Tooltip title="View change history">
            <IconButton
              size="small"
              aria-label={`History for ${item.itemCode}`}
              onClick={() =>
                onOpenAudit(item.itemCode, column.mdsColumn, item.label, item.scoreType)
              }
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
};

const SectionAccordion: React.FC<{
  section: MdsWorksheetSection;
  scoreOptions: Record<string, { value: string; description: string }[]>;
  editor: Editor;
  onOpenAudit: (itemCode: string, mdsColumn: string, label: string, scoreType: string) => void;
}> = ({ section, scoreOptions, editor, onOpenAudit }) => {
  const completion = sectionCompletion(section);
  return (
    <Accordion
      defaultExpanded={section.canEdit}
      disableGutters
      sx={{ border: '1px solid #E5E7EB' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ width: '100%' }}
        >
          <Typography variant="subtitle1">{section.sectionName}</Typography>
          {completion.total > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {completion.scored} of {completion.total} scored
            </Typography>
          )}
          {!section.canEdit && (
            <Tooltip title="Your role does not have edit access to this section">
              <Chip
                size="small"
                icon={<LockOutlinedIcon />}
                label="View only"
                variant="outlined"
              />
            </Tooltip>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Discipline</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Source</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {section.items.map((item) => (
              <ItemRow
                key={item.itemCode}
                item={item}
                scoreOptions={scoreOptions}
                canEdit={section.canEdit}
                editor={editor}
                onOpenAudit={onOpenAudit}
              />
            ))}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
};

const MdsWorksheet: React.FC = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    anchors,
    selectedAnchor,
    aggregate,
    loading,
    refreshing,
    error,
    selectAnchor,
    createWorksheet,
    reloadAggregate,
  } = useMdsWorksheet(patientId);
  const editor = useMdsWorksheetEditor(aggregate);
  const audit = useMdsWorksheetAudit(aggregate);
  const [auditItem, setAuditItem] = useState<{
    itemCode: string;
    mdsColumn: string;
    label: string;
    scoreType: string;
  } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [applyDocOpen, setApplyDocOpen] = useState(false);
  const [conflicts, setConflicts] = useState<MdsConflictItem[]>([]);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);
  // printing has its own independent kill switch (BRK-1859/1860)
  const [isPrintEnabled] = useFeatureToggleWhenReady(mdsDashboardPrintNewTreatment, 'FACILITY');
  // the exit to run if the user confirms past the unsaved-changes prompt.
  // `discard` distinguishes true exits (drop the draft now) from opening the
  // New worksheet dialog, where the draft must survive a cancelled create and
  // is dropped only when a successful create switches the anchor.
  const [pendingLeave, setPendingLeave] = useState<{
    run: () => void;
    discard: boolean;
  } | null>(null);

  const deadline = completeByLabel(aggregate?.assessment?.completeByDate ?? null);
  // defensive max, not versions[0] — baseVersionNumber correctness (conflict
  // detection) must not depend on the aggregate's array ordering
  const latestVersion = latestSubmissionVersion(aggregate?.versions);
  const canEditAny = (aggregate?.sections || []).some((section) => section.canEdit);

  // draft-aware completeness across editable sections (feeds the submit dialog)
  const { scored, total } = editableCompletion(aggregate?.sections, (itemCode, mdsColumn) =>
    editor.displayValue(itemCode, mdsColumn)
  );

  const handleSubmit = async (note: string) => {
    if (!selectedAnchor) {
      return;
    }
    // errors propagate to the dialog for inline display
    const result = await submitMdsWorksheet(selectedAnchor, {
      // null (not 0) = "no base version" per the contract; the backend skips
      // conflict detection for a first submission
      baseVersionNumber: latestVersion?.versionNumber ?? null,
      note: note.trim() ? note.trim() : null,
      items: editor.buildSubmissionItems(),
    });
    editor.discardAll();
    setConflicts(result.conflictItems || []);
    // reloadAggregate keeps Submit disabled (refreshing) until the new version
    // lands, so a fast re-edit can't submit against a stale baseVersionNumber
    reloadAggregate();
  };

  const handleLoadVersion = async (version: MdsWorksheetVersion) => {
    const loaded = await getMdsSubmissionItems(version.uuid);
    editor.applyValues(loaded.items);
  };

  const handleApplyDocument = (group: MdsDocGroup, mode: 'fill' | 'overwrite') => {
    group.items.forEach((docItem) => {
      if (mode === 'fill' && editor.displayValue(docItem.itemCode, docItem.mdsColumn) !== '') {
        return;
      }
      editor.applyGgValue(docItem.itemCode, docItem.mdsColumn, {
        value: docItem.value,
        ggCollectedId: docItem.ggCollectedId,
        levelType: 'CURRENT',
        documentType: group.documentType,
        dateOfService: group.dateOfService,
      });
    });
  };

  const hasApplyDocuments = canEditAny && collectApplyDocuments(aggregate?.sections).length > 0;

  const openAudit = (itemCode: string, mdsColumn: string, label: string, scoreType: string) => {
    setAuditItem({ itemCode, mdsColumn, label, scoreType });
    // lazily load the full submission trail on first open (no-op if cached)
    audit.load();
  };

  const handlePrint = async () => {
    if (!selectedAnchor) {
      return;
    }
    setPrinting(true);
    setPrintError(null);
    try {
      // renders the SAVED worksheet server-side; unsaved drafts are excluded,
      // which is why Print is disabled while dirty
      const result = await printMdsWorksheet(selectedAnchor);
      // opened after the await, so a popup blocker may return null — surface
      // that instead of failing silently ("clicked Print, nothing happened")
      const opened = window.open(result.pdfUrl, '_blank', 'noopener,noreferrer');
      if (!opened) {
        setPrintError(
          'Your browser blocked the print preview. Allow pop-ups for this site and try again.'
        );
      }
    } catch (e: any) {
      setPrintError(e.message || 'Failed to generate the MDS worksheet PDF');
    } finally {
      setPrinting(false);
    }
  };

  // sidebar navigation consults this flag (side-nav-item -> top-nav dialog)
  useEffect(() => {
    dispatch(setMdsWorksheetDirtyAction(editor.isDirty));
  }, [editor.isDirty, dispatch]);
  useEffect(
    () => () => {
      dispatch(setMdsWorksheetDirtyAction(false));
    },
    [dispatch]
  );

  // browser refresh/close
  useEffect(() => {
    if (!editor.isDirty) {
      return undefined;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [editor.isDirty]);

  // browser Back/Forward: a plain <BrowserRouter> has no useBlocker, so guard
  // history pops with the app's sentinel-entry pattern (edit-document-preview)
  // — re-push our entry and open the leave prompt instead of losing the draft
  useEffect(() => {
    if (!editor.isDirty) {
      return undefined;
    }
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      setPendingLeave({ run: () => navigate(paths.mdsDashboard), discard: true });
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
    // navigate is stable for a static path; re-running only on isDirty avoids
    // stacking sentinel entries on every keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.isDirty]);

  /**
   * Run `action` now when clean; otherwise prompt first. `discard` drops the
   * draft on confirm — true for real exits, false for the New worksheet dialog
   * (a cancelled create must keep the draft).
   */
  const guard = useCallback(
    (action: () => void, discard = true) => {
      if (editor.isDirty) {
        setPendingLeave({ run: action, discard });
      } else {
        action();
      }
    },
    [editor.isDirty]
  );

  const confirmLeave = () => {
    const pending = pendingLeave;
    setPendingLeave(null);
    if (pending?.discard) {
      editor.discardAll();
    }
    pending?.run();
  };

  return (
    <Stack
      gap="1.5rem"
      sx={{ width: '100%', padding: '2rem 2rem 2rem 4rem' }}
    >
      <div>
        <Link
          color="text.primary"
          component={RouterLink}
          href={paths.mdsDashboard}
          onClick={(e: React.MouseEvent) => {
            // let modified clicks (open-in-new-tab) through — they keep this
            // tab and its draft intact, so guarding them would be wrong
            const modified = e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
            if (editor.isDirty && !modified) {
              e.preventDefault();
              guard(() => navigate(paths.mdsDashboard));
            }
          }}
          sx={{ alignItems: 'center', display: 'inline-flex' }}
          underline="hover"
        >
          <SvgIcon sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </SvgIcon>
          <Typography variant="subtitle2">Back to MDS Dashboard</Typography>
        </Link>
      </div>
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        gap="1rem"
      >
        <Stack sx={{ minWidth: 220 }}>
          <Typography variant="h5">{aggregate?.patient?.patientName || 'MDS Worksheet'}</Typography>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {aggregate?.patient?.mrn ? `MRN ${aggregate.patient.mrn} · ` : ''}
            {aggregate?.patient?.facilityName || ''}
          </Typography>
        </Stack>

        {anchors && anchors.length > 0 && (
          <Select
            value={selectedAnchor || ''}
            onChange={(e) => guard(() => selectAnchor(e.target.value))}
            size="small"
            sx={{ minWidth: 260 }}
            inputProps={{ 'aria-label': 'Select assessment' }}
          >
            {anchors.map((anchor) => (
              <MenuItem
                key={anchor.assessmentUuid}
                value={anchor.assessmentUuid}
              >
                {assessmentTypeLabel(anchor.assessmentType, anchor.assessmentTypeRaw)}
                {' · ARD '}
                {shortDate(anchor.ard)}
              </MenuItem>
            ))}
          </Select>
        )}

        {deadline && (
          <Chip
            size="small"
            label={deadline.label}
            color={deadline.color}
          />
        )}

        <Chip
          size="small"
          variant="outlined"
          label={
            latestVersion
              ? `Submitted v${latestVersion.versionNumber}${
                  latestVersion.submittedByName || latestVersion.submittedByRole
                    ? ` · ${actorLabel(
                        latestVersion.submittedByName,
                        latestVersion.submittedByRole
                      )}`
                    : ''
                }`
              : 'Not started'
          }
        />

        {editor.isDirty && (
          <>
            <Chip
              size="small"
              color="warning"
              label={`${editor.dirtyCount} unsaved change${editor.dirtyCount === 1 ? '' : 's'}`}
            />
            <Button
              size="small"
              color="inherit"
              onClick={editor.discardAll}
            >
              Discard
            </Button>
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />
        {hasApplyDocuments && (
          <Button
            variant="text"
            startIcon={<DescriptionOutlinedIcon />}
            onClick={() => setApplyDocOpen(true)}
          >
            Apply from document
          </Button>
        )}
        {aggregate && aggregate.versions.length > 0 && (
          <Button
            variant="text"
            startIcon={<HistoryIcon />}
            onClick={() => setHistoryOpen(true)}
          >
            History
          </Button>
        )}
        {aggregate && aggregate.canPrint && isPrintEnabled && (
          <Tooltip
            title={
              editor.isDirty
                ? 'Submit your changes first — the print shows the saved worksheet'
                : ''
            }
          >
            {/* span wrapper so the tooltip still shows on a disabled button */}
            <span>
              <Button
                variant="text"
                startIcon={printing ? <CircularProgress size={16} /> : <PrintOutlinedIcon />}
                disabled={editor.isDirty || printing}
                onClick={handlePrint}
              >
                Print
              </Button>
            </span>
          </Tooltip>
        )}
        <Button
          variant="outlined"
          onClick={() => guard(() => setCreateOpen(true), false)}
        >
          New worksheet
        </Button>
        {aggregate && canEditAny && (
          <Button
            variant="contained"
            disabled={!editor.isDirty || refreshing}
            onClick={() => setSubmitOpen(true)}
          >
            Submit
          </Button>
        )}
      </Stack>

      {printError && (
        <Alert
          severity="error"
          onClose={() => setPrintError(null)}
        >
          {printError}
        </Alert>
      )}

      {conflicts.length > 0 && (
        <Alert
          severity="warning"
          onClose={() => setConflicts([])}
        >
          <AlertTitle>Submitted — {conflicts.length} item(s) overwrote newer edits</AlertTitle>
          {conflicts.map((conflict) => (
            <Typography
              key={`${conflict.itemCode}|${conflict.mdsColumn}`}
              variant="body2"
            >
              {conflict.itemCode}: your value {conflict.yourValue ?? '—'} replaced{' '}
              {conflict.previousValue ?? '—'}
              {conflict.previousByName || conflict.previousByRole
                ? ` (${actorLabel(conflict.previousByName, conflict.previousByRole)}`
                : ''}
              {conflict.previousVersion != null ? ` v${conflict.previousVersion})` : ''}
            </Typography>
          ))}
        </Alert>
      )}

      {error && <Typography color="error">{error}</Typography>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !aggregate ? (
        !error && (
          <Typography
            color="text.secondary"
            sx={{ py: 4 }}
          >
            No MDS worksheets exist for this patient yet — create one to begin. Patients do not need
            an open episode of care.
          </Typography>
        )
      ) : (
        <Stack gap="1rem">
          {aggregate.sections.map((section) => (
            <SectionAccordion
              key={section.sectionCode}
              section={section}
              scoreOptions={aggregate.scoreOptions}
              editor={editor}
              onOpenAudit={openAudit}
            />
          ))}
        </Stack>
      )}

      <NewWorksheetDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={createWorksheet}
      />

      <SubmitWorksheetDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSubmit={handleSubmit}
        changeCount={editor.dirtyCount}
        scored={scored}
        total={total}
      />

      <VersionHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        versions={aggregate?.versions || []}
        onLoad={handleLoadVersion}
      />

      <ApplyDocumentDialog
        open={applyDocOpen}
        onClose={() => setApplyDocOpen(false)}
        sections={aggregate?.sections}
        displayValue={(itemCode, mdsColumn) => editor.displayValue(itemCode, mdsColumn)}
        onApply={handleApplyDocument}
      />

      <WorksheetAuditDrawer
        open={auditItem !== null}
        onClose={() => setAuditItem(null)}
        itemCode={auditItem?.itemCode ?? null}
        itemLabel={auditItem?.label ?? null}
        entries={auditItem ? audit.historyFor(auditItem.itemCode, auditItem.mdsColumn) : []}
        options={auditItem ? aggregate?.scoreOptions?.[auditItem.scoreType] || [] : []}
        loading={audit.loading}
        error={audit.error}
      />

      <BootstrapDialog
        onClose={() => setPendingLeave(null)}
        open={pendingLeave !== null}
      >
        <PageLeaveConfirmModal
          close={() => setPendingLeave(null)}
          handleSaveBtn={confirmLeave}
        />
      </BootstrapDialog>
    </Stack>
  );
};

export default MdsWorksheet;
